import React, { Fragment, useEffect, useMemo, useState } from 'react'
import CaretDownIcon from '@tecuity/sos-icons/react/caret-down'
import './NewCollapsibleTable.scss'

// This table will only display strings.
// This table is not paginated.
const NewCollapsibleTable = (props) => {
  const [expandedRows, setExpandedRows] = useState([])

  const { rows, 
    mainTemplate, 
    subTemplates, 
    className = '', 
    wrapperClassName = '',
    expandable, 
    hideSubTableLabels = false, 
    hideMainHeader = false,
    showCollapseAllButton = false } = props
    
  return (
    <div className={`collapsible-table-wrapper ${wrapperClassName}`}>
      {showCollapseAllButton ?
        <div className="collapse-btn-row">
          <button aria-label="expand or collapse all rows" className="btn btn-raised btn-primary" onClick={() => {
            if (expandedRows.length > 0){
              setExpandedRows([])
            }
            else {
              setExpandedRows(Array.from({length: rows.length}, (_, i) => i))
            }
          }}>
            {`${expandedRows.length > 0 ? 'Collapse' : 'Expand'} All`}
          </button>
        </div> :
        null
      }
      <table className={`styled-table ${className}`}>
        {hideMainHeader ? null : (<TableHeader mainTemplate={mainTemplate} expandable={expandable} />)}
        <TableBody
          rows={rows}
          mainTemplate={mainTemplate}
          subTemplates={subTemplates}
          expandable={expandable}
          hideSubTableLabels={hideSubTableLabels}
          onExpandedChanged={(expanded, changedIndex) => {
            if(expanded) {
              setExpandedRows(expandedRows.concat([changedIndex]))
            }
            else{
              setExpandedRows(expandedRows.filter(i => i !== changedIndex))
            }
          }}
          expandedIndexes={expandedRows}
        />
      </table>
    </div>
  )
}

export default NewCollapsibleTable

const TableHeader = (props) => {
  const { mainTemplate, expandable } = props
  return (
    <thead>
      <tr className={expandable ? 'expandable' : ''}>
        {mainTemplate
          .filter((t) => t.IsEnabled)
          .map((t, i) => (
            <th key={i}>{t.DisplayName}</th>
          ))}
        {expandable && <th></th>}
      </tr>
    </thead>
  )
}

const TableBody = (props) => {
  const { rows, mainTemplate, subTemplates, expandable, hideSubTableLabels, onExpandedChanged, expandedIndexes } = props
  return (
    <tbody>
      {(rows || []).map((row, i) => {
        return (
          <NewRow
            row={row}
            mainTemplate={mainTemplate}
            subTemplates={subTemplates}
            expandable={expandable}
            key={row['Id'] || i}
            hideSubTableLabels={hideSubTableLabels}
            onExpandedChanged={onExpandedChanged}
            index={i}
            expandedIndexes={expandedIndexes}
          />
        )
      })}
    </tbody>
  )
}

const NewRow = (props) => {
  const { row, mainTemplate, subTemplates, expandable, hideSubTableLabels, onExpandedChanged, index, expandedIndexes } = props
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    setExpanded(expandedIndexes.includes(index))
  }, [expandedIndexes])

  const cells = useMemo(() => {
    return (mainTemplate || []).filter((t) => t.IsEnabled).map((t) => row[t.Accessor])
  }, [row, mainTemplate])

  return (
    <Fragment>
      <tr
        className="clickable"
        tabIndex={0}
        onClick={() => {
          setExpanded(!expanded)
          onExpandedChanged(!expanded, index)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setExpanded(!expanded)
            onExpandedChanged(!expanded, index)
          }
        }}
      >
        {cells.map((cell, i) => (
          <td key={i}>{cell}</td>
        ))}
        {expandable && (
          <td className="expand-cell">
            <div className="expand-wrapper">
              <CaretDownIcon className={expanded ? 'expanded' : ''} />
            </div>
          </td>
        )}
      </tr>
      {expanded && (
        <tr className="expansion-spacer">
          <td colSpan={cells.length}>{/* <div className="spacer" /> */}</td>
        </tr>
      )}
      {expanded && (
        <tr className={`expanded-row pop-in-down`}>
          <td colSpan={cells.length + (expandable ? 1 : 0)}>
            <div className="child-wrapper">
              {(subTemplates || []).map((st, i) => {
                return <SubTable 
                  rows={row[st.ArrayAccessor]} 
                  metadata={st} 
                  key={i} 
                  hideSubTableLabels={hideSubTableLabels}
                />
              })}
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

const SubTable = (props) => {
  const {
    rows,
    metadata: { DisplayName, EmptyMessage, Template },
    hideSubTableLabels
  } = props

  const filteredTemplate = useMemo(() => Template.filter((t) => t.IsEnabled), [Template])

  const filteredRows = useMemo(() => {
    const accessors = filteredTemplate.map((t) => t.Accessor)
    return rows.map((row) => {
      const newRow = {}
      accessors.forEach((a) => {
        newRow[a] = row[a]
      })
      return newRow
    })
  }, [rows, filteredTemplate])

  return (
    <Fragment>
      {hideSubTableLabels ? null : (<hr />)}
      {hideSubTableLabels ? null : (<h4>{DisplayName}</h4>)}
      <table>
        {filteredTemplate.length > 0 && (
          <thead>
            <tr>
              {filteredTemplate.map((t, i) => (
                <th key={i}>{t.DisplayName}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {filteredRows.length > 0 ? (
            filteredRows.map((row, j) => (
              <tr key={j}>
                {Object.keys(row).map((key, k) => (
                  <td key={k}>{row[key]}</td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={filteredTemplate.length}>{EmptyMessage || 'No rows.'}</td>
            </tr>
          )}
        </tbody>
      </table>
    </Fragment>
  )
}